import type {ReactNode} from 'react';

import {Dialog} from '@/components/base/dialog/Dialog';
import {injectProperty} from '@/container/injections';
import {IApiService} from '@/services/api/IApiService';
import type {RequestPostData, ServiceLandingName} from '@/services/api/interfaces/request';
import {HttpAdapterResponseExtractor} from '@/services/http-adapter/HttpAdapterResponseExtractor';
import type {IHttpAdapterResponse} from '@/services/http-adapter/interfaces';
import {ILightboxService} from '@/services/lightbox/ILightboxService';
import {type TAddReadonlyLightboxDefaultHandlers, ReadonlyLightbox} from '@/services/lightbox/interfaces';
import {ERROR, SUCCESS, XrayEventId} from '@/services/metrics/constants';
import {IMetricsService} from '@/services/metrics/IMetricsService';

import {RequestForm} from '../../forms/request-form/RequestForm';

interface RequestDialogProps {
	landingName: ServiceLandingName;
	title?: ReactNode;
	description?: ReactNode;
	buttonText?: string;
	commentText?: string;
	onSubmitSuccess?: (data: IHttpAdapterResponse['data']) => void;
	disclaimer?: JSX.Element | string;
	additionalTags?: string;
	noPhoneMask?: boolean;
	isSngCurrency?: boolean;
}

export class RequestDialog extends ReadonlyLightbox<RequestDialogProps> {
	@injectProperty(IApiService) protected apiService!: IApiService;
	@injectProperty(ILightboxService) protected lightboxService!: ILightboxService;
	@injectProperty(IMetricsService) protected metricsService!: IMetricsService;

	constructor(props: RequestDialogProps & TAddReadonlyLightboxDefaultHandlers<RequestDialogProps>) {
		super(props);

		this.metricsService.sendEvent({
			xrayEvents: [
				{
					name: XrayEventId.clickButtonSubmitApplication,
					params: {
						with_scroll_position: true,
						sng_currency: props.isSngCurrency,
					},
				},
			],
		});
	}

	handleSubmit = async (data: RequestPostData) => {
		const dataRequest = {
			...data,
			service: this.props.landingName,
			...(this.props.additionalTags ? {additional_tags: this.props.additionalTags} : {}),
		};

		const response = await this.apiService.request(dataRequest);

		this.props.onClose();

		const responseExtractor = new HttpAdapterResponseExtractor(response);

		const status = responseExtractor.isSuccessResponse ? SUCCESS : ERROR;
		this.metricsService.sendEvent({
			xrayEvents: [
				{
					name: XrayEventId.clickButtonSend,
					i: status,
					params: {
						type: 'modalForm',
						status,
						sng_currency: this.props.isSngCurrency,
					},
				},
			],
		});

		if (responseExtractor.isSuccessResponse) {
			this.props.onSubmitSuccess(responseExtractor.getResponseData());
		} else {
			this.lightboxService.openError(responseExtractor.getErrorMessage());
		}
	};

	render() {
		const {title, description, buttonText, commentText, disclaimer} = this.props;
		return (
			<Dialog.Body>
				{title && <Dialog.Title>{title}</Dialog.Title>}
				{description && <Dialog.Description>{description}</Dialog.Description>}
				<RequestForm
					autoFocusFirstInput
					onSubmit={this.handleSubmit}
					buttonText={buttonText}
					commentText={commentText}
					disclaimer={disclaimer}
					onClose={this.props.onClose}
					noPhoneMask={this.props.noPhoneMask}
					isModal
					testId="request-form"
				/>
			</Dialog.Body>
		);
	}
}
